import { Alert, Button } from 'antd';
import React from 'react';

const RELOAD_TIMESTAMP_KEY = 'chunkErrorReloadedAt';
const RELOAD_COOLDOWN_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/.test(error.message)
  );
}

function shouldAttemptReload(): boolean {
  const lastReloadedAt = Number(
    window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0,
  );

  return Date.now() - lastReloadedAt > RELOAD_COOLDOWN_MS;
}

function markReloadAttempted(): void {
  window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
}

interface IChunkErrorBoundaryProps {
  children: React.ReactNode;
}

interface IChunkErrorBoundaryState {
  hasError: boolean;
  isReloading: boolean;
}

export class ChunkErrorBoundary extends React.Component<
  IChunkErrorBoundaryProps,
  IChunkErrorBoundaryState
> {
  constructor(props: IChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isReloading: false };
  }

  static getDerivedStateFromError(): IChunkErrorBoundaryState {
    return { hasError: true, isReloading: false };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error) && shouldAttemptReload()) {
      markReloadAttempted();
      this.setState({ isReloading: true });
      window.location.reload();
    }
  }

  render(): React.ReactNode {
    const { hasError, isReloading } = this.state;

    if (hasError) {
      if (isReloading) {
        return null;
      }

      return (
        <Alert
          title="Something went wrong"
          description="The page failed to load correctly. Please reload the page. If the problem persists, contact support."
          type="error"
          showIcon
          action={
            <Button
              size="small"
              danger
              onClick={(): void => window.location.reload()}
            >
              Reload
            </Button>
          }
        />
      );
    }

    return this.props.children;
  }
}
